import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import {
  Animated,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native'
import Feedback from './feedback'
import type { SelectedIcon as TxValueIcon } from '@ecountabl/lib'
import { MatchedPlaidTransaction, currencyFormat, extractDate, logoUrl } from '@ecountabl/lib'
import { ThemeContext } from '../utility/context/ThemeContext'
import { Ionicons, MaterialIcons } from "@expo/vector-icons"
import { MAX_CONTENT_WIDTH, extra } from '../config'
import { ShadowView, textStyles } from '../style'
import { useAppSelector } from '../store'
import DynamicImage from '../utility/dynamic-image'
import { StackActions, useLinkProps } from '@react-navigation/native'
import Haptics from "../utility/haptics"
import Analytics from '../utility/analytics'
import { ValueBadge } from '../utility/value-badge'

enum FeedbackState {
  Closed,
  Opening,
  Open,
  Closing
}

type Props = {
  tx: MatchedPlaidTransaction;
  navigation: any;
  showDate?: boolean;
  toggleAddBrand?: (
    show: boolean,
    opts?: {tx?: MatchedPlaidTransaction, isLocal?: boolean}
  ) => void;
  onErrorNavigating: () => void;
}

const Transaction: React.FC<Props> = ({
  tx, showDate=true, toggleAddBrand, onErrorNavigating
}: Props) => {
  const {accounts} = useAppSelector(s => s.accountsSlice)
  const { user } = useAppSelector(s => s.auth)
  const valuesMap = useAppSelector(s => s.values.map) as Record<string, any>

  const linkProps = useLinkProps({
    screen: 'Company',
    params: {
      id: tx.match_id,
      source: "transaction",
      name: tx.match
    },
    action: StackActions.push('Company', {
      id: tx.match_id,
      source: "transaction",
      name: tx.match
    })
  })

  const { theme } = useContext(ThemeContext)
  const { textMain, primary, danger, success, white, dark, bgTransaction } = theme.colors

  const [feedbackState, setFeedbackState] = useState(FeedbackState.Closed)
  const feedbackHeight = useRef(new Animated.Value(0)).current

  const memoizedDate = useMemo(() => {return extractDate(tx.date)}, [tx.date])
  const memoizedColorBand = useMemo(() => {
    return accounts.find(acc => acc.account_id === tx.account_id)?.color || undefined
  },[tx.account_id])

  const userSelectedValueSlugs = useMemo(() => {
    const slugs = new Set<string>()
    try {
      const userMap = user?.valuedImpactAreaMap || {}
      Object.values(valuesMap || {}).forEach((v: any) => {
        const slug = typeof v?.slug === 'string' ? v.slug : undefined
        if (!slug) return
        const ias = Array.isArray(v?.impactAreas) ? v.impactAreas : []
        const anySelected = ias.some((ia: any) => !!userMap?.[ia?._id])
        if (anySelected) slugs.add(slug)
      })
    } catch (_) { /* noop */ }
    return slugs
  }, [user, valuesMap])

  const displayValuesAligned = useMemo(() => {
    const aligned = Array.isArray((tx as any)?.valuesAligned) ? (tx as any).valuesAligned : []
    if (!aligned.length) return []
    // If we don't yet know the user's selected values, fall back to showing all aligned.
    if (!userSelectedValueSlugs.size) return aligned
    return aligned.filter((v: any) => {
      const slug = typeof v?.slug === 'string' ? v.slug : ''
      return !!slug && userSelectedValueSlugs.has(slug)
    })
  }, [tx, userSelectedValueSlugs])

  const toggleExpanded = () => {
    if (!toggleAddBrand) {
      return
    }
    
    setFeedbackState((prev) => {
      switch (prev) {
        case FeedbackState.Closed:
          return FeedbackState.Opening
        case FeedbackState.Opening:
          return FeedbackState.Open
        case FeedbackState.Closing:
          return FeedbackState.Closed
        case FeedbackState.Open:
          return FeedbackState.Closing
        default:
          return FeedbackState.Closed
      }
    })
  }

  useEffect(() => {
    const toValue = (
      feedbackState == FeedbackState.Opening || feedbackState == FeedbackState.Open ? 400 : 0
    )

    Animated.timing(feedbackHeight, {
      toValue: toValue,
      duration: toValue === 0 ? 200 : 500,
      useNativeDriver: false
    }).start(() => {
      if (feedbackState === FeedbackState.Opening) {
        setFeedbackState(FeedbackState.Open)
      } else if (feedbackState === FeedbackState.Closing) {
        setFeedbackState(FeedbackState.Closed)
      }
    })
  },[feedbackState])

  const logoSrc = useMemo(() => {
    if (tx.match && (tx.match_id?.toString()?.startsWith('IMPACT_NETWORK') ?? false)) {
      return tx.logourl
    }
    return logoUrl(tx.logourl, extra.cdn)
  }, [tx.logourl])

  return (
    <View>
      <ShadowView style={[{borderRadius: 10}]}>
        <TouchableOpacity
          style={[
            styles.transactionCard,
            {maxWidth: MAX_CONTENT_WIDTH, backgroundColor: bgTransaction}
          ]}
          onLongPress={() => {
            Haptics.impactHeavy()
            toggleExpanded()
          }}
          onPress={() => {
            // if no match, feedback opening, otherwise it is a nav link
            if (!tx.match) {
              Haptics.impactMedium()
              toggleExpanded()
            } else {
              Haptics.selection()
              Analytics.track('company_clicked', {
                companyId: tx.match_id.toString(),
                companyName: tx.match,
                source: "transaction",
              })
            }
          }}
          {
            ...(
              tx.match ?
              linkProps
              : {}
            )
          }
        >
          <View style={[styles.colorBand,{backgroundColor: memoizedColorBand}]}/>
          <View style={styles.cardCol}>
            {
              tx.match ? (
                <DynamicImage
                  src={logoSrc}
                  alt={`${tx.match} logo`}
                  style={{width: 50, height: 50, borderRadius: 8, resizeMode: "contain", backgroundColor: white}}
                  fallbackIcon={<MaterialIcons name="storefront" size={28} color={dark}/>}
                  errorIcon={<MaterialIcons name="error-outline" size={50} color={danger}/>}
                />
              ) : (
                <Ionicons
                  name={"wallet-outline"}
                  size={50}
                  color={primary}
                />
              )
            }
          </View>
          <View style={[styles.cardCol, {flex: 1, justifyContent: "center", gap: 5}]}>
            <Text
              style={[
                textStyles.body,
                styles.transactionName,
                {color: textMain}
              ]}
              numberOfLines={1}
              ellipsizeMode="tail"
            >
              {/*
                tx.isValuesAligned && <>
                  <AntDesign name='checksquare' color={primary} size={15}/>
                  {' '}
                </>
              */}
              {tx.name}
            </Text>
            <View style={{flexDirection: "row", flexWrap: "wrap", justifyContent: "flex-start", columnGap: 5, rowGap: 2}}>
              {
                displayValuesAligned.length > 0 ?
                <View style={styles.valueDotsRow}>
                  {displayValuesAligned.map((v: unknown, idx: number) => {
                      const rec = (v || {}) as Record<string, unknown>
                      const color = typeof rec.color === 'string' ? rec.color : undefined
                      const icon = rec.icon as TxValueIcon | null | undefined
                      const slug = typeof rec.slug === 'string' ? rec.slug : undefined
                      const label = typeof rec.label === 'string' ? rec.label : undefined

                      return (
                        <ValueBadge
                          key={slug || label || String(idx)}
                          color={color}
                          accentFallbackColor={primary}
                          icon={icon ?? null}
                          label={label}
                          size={22}
                          iconSize={14}
                        />
                      )
                    })}
                </View>
                : <Text style={[textStyles.subscript, {fontWeight: "bold", color: textMain}]}>
                    {tx.match ? "Discover alternative businesses" : "Add Company Info"}
                  </Text>
              }
            </View>
            {
              tx.account_info ?
              <Text style={[textStyles.subscript, {color: textMain}]}>
                {tx.account_info.institution} - {tx.account_info.name}
              </Text>
              : null
            }
          </View>
          <View style={styles.cardCol}>
            <View style={{flex: 1}}/>
            <View style={{flex: 1, justifyContent: "center"}}>
              <Text
                style={[
                  textStyles.body,
                  styles.amount,
                  { color: tx.amount < 0 ? success : danger }
                ]}
              >
                {currencyFormat((tx.amount * -1))}
              </Text>
            </View>
            <View style={{flex: 1, justifyContent: "flex-end"}}>
              <Text style={[textStyles.subscript, styles.transactionDate,{color: textMain}]}>
                {showDate ? memoizedDate.toLocaleDateString() : ""}
              </Text>
            </View>
            
          </View>
        </TouchableOpacity>
      </ShadowView>
      
      <Animated.View style={[{maxHeight: feedbackHeight, overflow: "hidden"}]}>
        { feedbackState != FeedbackState.Closed &&
          <Feedback
            tx={tx}
            onCancel={toggleExpanded}
            toggleAddBrand={(show,opts) => {
              toggleAddBrand(show,opts)
              if (opts?.isLocal) {
                toggleExpanded()
              }
            }}
          />
        }
      </Animated.View>
    </View>
  )
}



const styles = StyleSheet.create({
  txWrap: {},
  txDetail: {},
  transaction: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  transactionLeft: {
    flex: 6,
    flexDirection: 'column',
    alignItems: 'flex-start',
    paddingRight: 10
  },
  transactionRight: {
    flex: 3,
    flexDirection: 'row-reverse',
    alignItems: 'center',
  },
  transactionDate: {
    textAlign: "right",
  },
  transactionName: {
    fontSize: 14,
    textAlign: 'left',
  },
  transactionImage: {
    width: 50,
    height: 50,
    borderRadius: 100,
    marginLeft: 5,
    marginRight: 5,
  },
  iconStyle: {
    marginLeft: 10,
    marginRight: 10
  },
  transactionCard: {
    alignSelf: "center",
    width: "100%",
    flexDirection: "row",
    justifyContent: "space-between",
    gap: 15,
    padding: 15,
    borderRadius: 10,
    overflow: "hidden",
  },
  cardCol: {
    flexDirection: "column",
    justifyContent: "center",
  },
  amount: {
    fontWeight: "600",
    textAlign: "right"
  },
  iconAndText: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  colorBand: {
    position: 'absolute',
    top: 0,
    left: 0,
    bottom: 0,
    width: 10,
  },
  valueDotsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 4,
  },
})

export default Transaction
